import { CYCLE_EXPIRED_STATUS, INTERCEPTED_URL_MARKERS, LIVE_HOME_PATH } from "./constants";
import { readPreviewRequestHeaders } from "./previewHeaders";
import { clearCycleId, readCycleId, readSlot } from "./session";

/**
 * Monkey-patches {@code window.fetch} so any call to the storefront BFF is
 * automatically tagged with the staging-preview headers when the session has
 * a cycle id, and so any 410 response flushes the session and bounces the
 * user to live.
 *
 * <p>Why monkey-patching over a wrapper API: retrofitting every single fetch
 * call across the storefront (hundreds of sites across cart/search/PDP/CMS)
 * would be a large, error-prone PR. A single interceptor at bootstrap covers
 * 100% of browser fetches without touching callsites.
 *
 * <p>Scope of interception:
 * <ul>
 *   <li>Only requests whose URL contains one of {@link INTERCEPTED_URL_MARKERS}
 *       ({@code "/api/"} plus the legacy {@code "/touch/getMarketingText"} shim)
 *       — keeps analytics / fonts / CDN calls alone.
 *   <li>Only mutates headers — request body, method, credentials unchanged.
 *   <li>410 triggers session flush + full-page reload to live.
 * </ul>
 *
 * <p>SSR-safe: no-op when called under Gatsby's server-side build (no window).
 */
export function installStagingFetchInterceptor(): void {
  if (typeof window === "undefined" || !window.fetch) return;
  // Don't double-install on HMR reloads.
  if ((window.fetch as unknown as { __stagingPatched?: boolean }).__stagingPatched) return;

  const originalFetch = window.fetch.bind(window);

  const patched: typeof fetch = async (input, init) => {
    const cycleId = readCycleId();
    const slot = readSlot();
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    const intercepted = isInterceptedUrl(url);
    const inPreview = cycleId !== null || slot !== null;
    const shouldStamp = inPreview && intercepted;

    // Force network bypass of HTTP cache ONLY while the session is in staging mode.
    // A prior broken staging attempt may have cached `410 Gone` responses with no
    // Cache-Control header — those stale entries would keep returning 410 "from
    // disk cache" for the rest of the browser session. Setting cache: 'no-store'
    // on every /api/ request while staging is active guarantees the browser hits the
    // network. When NOT in staging we leave the HTTP cache alone so live traffic
    // keeps benefiting from normal 304 / ETag revalidation.
    let finalInit: RequestInit | undefined = shouldStamp ? withStagingHeaders(init) : init;
    if (intercepted && inPreview) {
      finalInit = { ...(finalInit ?? {}), cache: "no-store" };
    }
    const response = await originalFetch(input, finalInit);

    if (shouldStamp && cycleId !== null && response.status === CYCLE_EXPIRED_STATUS) {
      // Cycle is dead — flush and bounce. Let this request resolve normally;
      // the user will land on live before any consumer acts on the 410 body.
      // (Slot-only previews don't carry a cycle, so 410 is impossible there;
      // this branch is staging-cycle territory.)
      clearCycleId();
      if (typeof window !== "undefined") {
        window.location.assign(LIVE_HOME_PATH);
      }
    }

    return response;
  };

  (patched as unknown as { __stagingPatched: boolean }).__stagingPatched = true;
  window.fetch = patched;
}

function isInterceptedUrl(url: string): boolean {
  return INTERCEPTED_URL_MARKERS.some((marker) => url.includes(marker));
}

function withStagingHeaders(init: RequestInit | undefined): RequestInit {
  const headers = new Headers(init?.headers ?? undefined);
  for (const [name, value] of Object.entries(readPreviewRequestHeaders())) {
    headers.set(name, value);
  }
  return { ...(init ?? {}), headers };
}

/**
 * XHR counterpart to {@link installStagingFetchInterceptor}. Monkey-patches
 * {@code XMLHttpRequest.prototype.open} so that every XHR to a storefront BFF
 * URL carries the staging headers when the session is in staging mode.
 *
 * <p>Most storefront code uses {@code fetch}, but some third-party analytics /
 * tracking libraries still issue XHRs — without this patch they would hit
 * live mid-preview and either (a) leak live user behaviour into preview
 * segments, or (b) receive live data and render it into the preview DOM. This
 * is safety-in-depth; breakage-mode if it's missing is "analytics contamination"
 * rather than a visible bug, which is why the gap lived in the README for a
 * sprint before getting closed.
 *
 * <p>Intercepts the same URL marker set as {@code fetch} — only traffic that
 * contains {@code /api/}. Third-party CDN / fonts / analytics-upload endpoints
 * pass through untouched.
 *
 * <p>SSR-safe: no-op when {@code window} or {@code XMLHttpRequest} is absent.
 * Idempotent: tagged with {@code __stagingXhrPatched} so HMR reloads don't
 * stack multiple wrappers.
 */
export function installXhrInterceptor(): void {
  if (typeof window === "undefined" || typeof XMLHttpRequest === "undefined") return;
  const proto = XMLHttpRequest.prototype as unknown as {
    open: typeof XMLHttpRequest.prototype.open;
    send: typeof XMLHttpRequest.prototype.send;
    __stagingXhrPatched?: boolean;
    __stagingXhrUrl?: string;
  };
  if (proto.__stagingXhrPatched === true) return;

  const originalOpen = proto.open;
  const originalSend = proto.send;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, func-names
  proto.open = function (this: XMLHttpRequest, method: string, url: string | URL, ...rest: unknown[]): void {
    const urlString = typeof url === "string" ? url : url.href;
    (this as unknown as { __stagingXhrUrl: string }).__stagingXhrUrl = urlString;
    // Preserve original arity semantics: forward all remaining args unchanged.
    return originalOpen.apply(
      this,
      [method, url, ...rest] as unknown as Parameters<typeof XMLHttpRequest.prototype.open>
    );
  };

  // eslint-disable-next-line func-names
  proto.send = function (this: XMLHttpRequest, body?: Document | XMLHttpRequestBodyInit | null): void {
    try {
      const storedUrl = (this as unknown as { __stagingXhrUrl?: string }).__stagingXhrUrl ?? "";
      if (isInterceptedUrl(storedUrl)) {
        for (const [name, value] of Object.entries(readPreviewRequestHeaders())) {
          this.setRequestHeader(name, value);
        }
      }
    } catch {
      // setRequestHeader throws if the state isn't OPENED, or for forbidden
      // header names — both are recoverable: the request proceeds without the
      // staging headers and the server-side guard will refuse to serve
      // staging data, which is the safe-default behaviour.
    }
    return originalSend.call(this, body ?? null);
  };

  proto.__stagingXhrPatched = true;
}
